import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, CircularProgress, alpha } from '@mui/material';
import axios from 'axios';
import ReportCard from './components/ReportCard';
import DateFilter from './components/DateFilter';
import SearchBar from './components/SearchBar';

// API configuration - use relative path so requests go through the nginx proxy
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const API_VERSION = 'api/v1';

interface Report {
  ticker: string;
  company_name: string;
  title: string;
  date_created: string;
  sentiment: string;
  summary: string;
  report_id: string;
  url?: string;
}

const App: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('today');
  const [error, setError] = useState('');

  const fetchReports = async (date: string, search?: string) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        date_filter: date,
        ...(search && { search }),
      });
      
      const response = await axios.get(`${API_BASE_URL}/${API_VERSION}/reports?${params}`);
      setReports(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch reports. Please try again later.');
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(dateFilter);
  }, [dateFilter]);

  const handleDateChange = (newValue: string) => {
    setDateFilter(newValue);
  };

  const handleSearch = (query: string) => {
    fetchReports(dateFilter, query);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0F1419 0%, #1A1F2E 100%)',
        backgroundAttachment: 'fixed',
        py: 6,
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Section */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              background: 'linear-gradient(135deg, #00D9FF 0%, #667EEA 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
              fontWeight: 700,
              letterSpacing: '-1px',
            }}
          >
            Market Updates
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.95rem',
            }}
          >
            Track company reports and trading updates
          </Typography>
        </Box>

        {/* Controls Section */}
        <Box
          sx={{
            mb: 4,
            p: 3,
            background: alpha('#1A1F2E', 0.8),
            border: '1px solid rgba(0, 217, 255, 0.1)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
          }}
        >
          <DateFilter value={dateFilter} onChange={handleDateChange} />
          <SearchBar onSearch={handleSearch} />
        </Box>

        {/* Error State */}
        {error && (
          <Box
            sx={{
              mb: 3,
              p: 3,
              background: 'rgba(255, 107, 107, 0.1)',
              border: '1px solid rgba(255, 107, 107, 0.3)',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography sx={{ color: '#FF6B6B', fontWeight: 500 }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Loading State */}
        {loading ? (
          <Box display="flex" justifyContent="center" my={8}>
            <CircularProgress
              sx={{
                color: '#00D9FF',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }}
            />
          </Box>
        ) : reports.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              p: 3,
              background: alpha('#1A1F2E', 0.6),
              border: '1px dashed rgba(0, 217, 255, 0.2)',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '1.1rem',
              }}
            >
              No reports found for the selected criteria.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 3 }}>
            {reports.map((report, index) => (
              <ReportCard key={index} report={report} />
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default App;
